import { useMemo, useState } from 'react'
import { Box, Dialog, DialogContent, InputBase, Stack } from '@mui/material'

import { DialogHeader } from '@/components/atoms/DialogHeader'
import { TabBar } from '@/components/molecules/TabBar'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { NOTION_DIALOG_PAPER_SX } from '@/shared/styles/dialogStyles'
import { DURATION } from '@/shared/styles/motion'
import { RADIUS } from '@/shared/styles/radii'
import { FONT_WEIGHT, scaledFontSize } from '@/shared/styles/typography'
import {
  SOAP_TYPE_ORDER,
  SOAP_TYPE_TO_SECTION,
  linesFromSoapSet,
  type MasterInsertLine,
  type SoapMasterType,
  type SoapSet,
} from '@/features/soap-masters'

import { SoapSetLines } from './SoapSetLines'
import { authorsOf, filterSoapSets } from './soapSetFilter'
import { isUntypedSet, soapSetLabel, soapSetMeta } from './soapSetLabel'

// P-CUBEn / AnyCOMPASS にあった「選んで入れる」口の復元 (Zendesk #2237 ③)。
//
// **ここは「使う」面。マスタへの書き込みはゼロ**で、整えるのは設定 > 定型文が担う。
// 1 人の薬剤師が薬歴を書いていて押した操作が店舗全員の資産を変える形にしない。
//
// **画面は従来のまま。器だけを 1 つにした** (松田さん 2026-08-26)。
// タブは DB の表ではなく**行が区分を持つか**で引く:
//   SOAPセット = 区分あり / 文章マスタ = 区分なし (旧 TextMaster からの移行分)
// 保谷の見え方・クリック数は 1 つも変わらない。
//
// 引き算の方針:
// - 絞り込みは検索 1 本 + 作成者 chips のみ。条件・分類の chips は置かない
//   (検索が条件名にも当たるため。列を増やすほど探す前に読む量が増える)
// - SOAPセットは 1 クリックで全行挿入。確認は挟まない (追記なので行削除で戻せる)
// - 文章マスタは SOAP 区分を持たないので、行を押すと**その行が** 6 つの区分ボタンに
//   開く (別ダイアログを重ねない)

type Tab = 'sets' | 'texts'

interface Props {
  open: boolean
  onClose: () => void
  soapSets: SoapSet[]
  // 挿入の実行。成功したらダイアログを閉じる。
  onInsert: (lines: MasterInsertLine[], label: string) => Promise<boolean>
  isInserting: boolean
}

const rowSx = {
  display: 'block',
  width: '100%',
  textAlign: 'left' as const,
  appearance: 'none',
  border: 'none',
  bgcolor: 'transparent',
  fontFamily: 'inherit',
  cursor: 'pointer',
  px: 1,
  py: 0.75,
  borderRadius: `${RADIUS.md}px`,
  transition: `background-color ${DURATION.fast}ms`,
  '&:hover': { bgcolor: colorTokens.overlay.hover },
  '&:focus': { outline: 'none' },
  '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
  '&:disabled': { cursor: 'default', opacity: 0.5 },
}

// 絞り込み chips 専用 (作成者)。低 emphasis の neutral chrome で、選択は地色が示す。
// タブは TabBar・挿入の実行は sectionButtonSx が担う (1 つの sx を 3 役に兼務させない)。
const filterChipSx = (selected: boolean) => ({
  appearance: 'none',
  border: 'none',
  fontFamily: 'inherit',
  fontSize: scaledFontSize('tag'),
  fontWeight: FONT_WEIGHT.medium,
  minHeight: 24,
  px: 1,
  borderRadius: `${RADIUS.full}px`,
  cursor: 'pointer',
  color: selected ? colorTokens.fg.default : colorTokens.fg.muted,
  bgcolor: selected ? colorTokens.overlay.selected : 'transparent',
  transition: `background-color ${DURATION.fast}ms`,
  '&:hover': {
    bgcolor: selected
      ? colorTokens.overlay.selected
      : colorTokens.overlay.hover,
  },
  '&:focus': { outline: 'none' },
  '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
})

// 区分の無い行の挿入先を選ぶボタン。このダイアログのコミット操作なので、
// 絞り込み chips と同じ最低 emphasis には置かない (button.md secondary トーン)。
// 青は使わない: 1 画面に 6 個の青は color-discipline の「青 3 つ以上は違反を疑う」に反する。
const sectionButtonSx = {
  appearance: 'none',
  border: 'none',
  fontFamily: 'inherit',
  fontSize: scaledFontSize('label'),
  fontWeight: FONT_WEIGHT.medium,
  minHeight: 28,
  px: 1.75,
  borderRadius: `${RADIUS.full}px`,
  cursor: 'pointer',
  color: colorTokens.fg.default,
  bgcolor: colorTokens.bg.muted,
  transition: `background-color ${DURATION.fast}ms`,
  '&:hover': { bgcolor: colorTokens.bg.emphasis },
  '&:focus': { outline: 'none' },
  // 真のコミット操作なので focus は青リング (リスト行の neutral リングとは使い分けが違う)。
  '&:focus-visible': { boxShadow: colorTokens.focus.ringShadow },
  // 挿入中は行と同時にボタンも沈める (行だけ沈んでボタンが明るいままにしない)。
  '&:disabled': {
    cursor: 'not-allowed',
    color: colorTokens.fg.faint,
    bgcolor: colorTokens.bg.muted,
  },
}

export const MasterInsertDialog = ({
  open,
  onClose,
  soapSets,
  onInsert,
  isInserting,
}: Props) => {
  // 区分を持つか持たないかで従来の 2 面に振り分ける (器は 1 つのまま)。
  const typedSets = useMemo(
    () => soapSets.filter((s) => !isUntypedSet(s)),
    [soapSets]
  )
  const untypedSets = useMemo(() => soapSets.filter(isUntypedSet), [soapSets])
  const hasSets = typedSets.length > 0

  const [tab, setTab] = useState<Tab>(hasSets ? 'sets' : 'texts')
  const [query, setQuery] = useState('')
  const [author, setAuthor] = useState<string | null>(null)
  // 区分ボタンを開いている文章マスタの id (= その行だけ 6 ボタンに開く)。
  const [openTextId, setOpenTextId] = useState<string | null>(null)

  const authors = useMemo(() => authorsOf(typedSets), [typedSets])

  const visibleSets = useMemo(
    () => filterSoapSets(typedSets, { query, author }),
    [typedSets, author, query]
  )

  // 文章マスタ側は作成者の軸を持たない (移行元に作成者列が無い) ので author を渡さない。
  const visibleTexts = useMemo(
    () => filterSoapSets(untypedSets, { query }),
    [untypedSets, query]
  )

  const close = () => {
    setQuery('')
    setAuthor(null)
    setOpenTextId(null)
    onClose()
  }

  const insertSet = async (soapSet: SoapSet) => {
    const ok = await onInsert(
      linesFromSoapSet(soapSet),
      soapSetLabel(soapSet) || 'SOAPセット'
    )
    if (ok) close()
  }

  const insertText = async (soapSet: SoapSet, type: SoapMasterType) => {
    const ok = await onInsert(
      [
        {
          section: SOAP_TYPE_TO_SECTION[type],
          content: soapSet.lines[0].content,
        },
      ],
      // 文章マスタ側の toast は従来どおり**見出しだけ**を出す (条件を連結しない)
      soapSet.condition2 || soapSet.lines[0].content
    )
    if (ok) close()
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX }}
    >
      <DialogHeader title="定型文" onClose={close} />
      {/* タブは両方に中身があるときだけ出す (片方しか無ければ切替は無意味)。
          DialogContent の**外**に置くのは TabBar の罫線を full-bleed に保つため
          (中に入れると左右 24px の padding の内側で罫線だけ両端が途切れ、直上の
          header 罫線と食い違う)。header 罫線 → タブ → 中身 の 3 層は ga の録音ブロック
          (Header + MeetingTabBar) と同じ構成。 */}
      {hasSets && untypedSets.length > 0 && (
        <TabBar<Tab>
          tabs={[
            { key: 'sets', label: `SOAPセット (${typedSets.length})` },
            { key: 'texts', label: `文章マスタ (${untypedSets.length})` },
          ]}
          active={tab}
          onChange={(key) => {
            setTab(key)
            setOpenTextId(null)
          }}
        />
      )}
      <DialogContent sx={{ pt: 1.5, pb: 2 }}>
        {/* inline 編集の器 (枠線なし・地色だけで affordance を伝える). 平常は bg.subtle で、
            hover / focus で bg.muted に濃くする. focus は入力欄なので青リング
            (focus.ringShadow) — リスト行の neutral リングとは使い分けが違う.
            placeholder の色は明示する: MUI 既定の currentColor × opacity 0.42 は
            bg.muted 上で 2.26:1 しか出ず、token を経由しないので grep でも見つからない. */}
        <InputBase
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="検索 (条件・作成者・本文)"
          fullWidth
          sx={{
            fontSize: scaledFontSize('bodySm'),
            px: 1,
            py: 0.5,
            borderRadius: `${RADIUS.md}px`,
            bgcolor: colorTokens.bg.subtle,
            transition: `background-color ${DURATION.fast}ms, box-shadow ${DURATION.fast}ms`,
            '&:hover': { bgcolor: colorTokens.bg.muted },
            '&.Mui-focused': {
              bgcolor: colorTokens.bg.muted,
              boxShadow: colorTokens.focus.ringShadow,
            },
            '& input::placeholder': {
              color: colorTokens.fg.faint,
              opacity: 1,
            },
          }}
        />

        {/* 作成者は移行元 CSV で 4 名混在しており「自分のセット」を出す軸として要る。
            条件は検索で当たるので chips を置かない (= 絞り込み列は 1 本だけ)。 */}
        {tab === 'sets' && authors.length >= 2 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            <Box
              component="button"
              type="button"
              onClick={() => setAuthor(null)}
              sx={filterChipSx(author === null)}
            >
              すべて
            </Box>
            {authors.map((name) => (
              <Box
                key={name}
                component="button"
                type="button"
                onClick={() => setAuthor(name)}
                sx={filterChipSx(author === name)}
              >
                {name}
              </Box>
            ))}
          </Box>
        )}

        <Stack spacing={0.25} sx={{ mt: 1, maxHeight: 384, overflowY: 'auto' }}>
          {tab === 'sets'
            ? visibleSets.map((s) => (
                <Box
                  key={s.id}
                  component="button"
                  type="button"
                  disabled={isInserting}
                  onClick={() => void insertSet(s)}
                  sx={rowSx}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 0.75,
                      fontSize: scaledFontSize('bodySm'),
                      fontWeight: FONT_WEIGHT.semibold,
                      color: colorTokens.fg.default,
                    }}
                  >
                    <Box component="span">
                      {soapSetLabel(s) || '(条件なし)'}
                    </Box>
                    {/* 作成者は絞り込み軸そのものなので三次色に落とさない (fg.muted で 5.41:1). */}
                    <Box
                      component="span"
                      sx={{
                        fontSize: scaledFontSize('tag'),
                        fontWeight: FONT_WEIGHT.medium,
                        color: colorTokens.fg.muted,
                      }}
                    >
                      {soapSetMeta(s)}
                    </Box>
                  </Box>
                  {/* 全行をそのまま見せる。押した結果がそのまま入るので、抜粋にして
                      「思ったのと違うものが入った」を作らない。 */}
                  <SoapSetLines lines={s.lines} />
                </Box>
              ))
            : visibleTexts.map((m) => {
                const isOpen = openTextId === m.id
                const title = m.condition2 || m.lines[0].content
                return (
                  <Box key={m.id}>
                    <Box
                      component="button"
                      type="button"
                      disabled={isInserting}
                      aria-expanded={isOpen}
                      onClick={() => setOpenTextId(isOpen ? null : m.id)}
                      sx={rowSx}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 0.75,
                          fontSize: scaledFontSize('bodySm'),
                          fontWeight: FONT_WEIGHT.semibold,
                          color: colorTokens.fg.default,
                        }}
                      >
                        <Box component="span">{title}</Box>
                        {m.condition1 && (
                          <Box
                            component="span"
                            sx={{
                              fontSize: scaledFontSize('tag'),
                              fontWeight: FONT_WEIGHT.medium,
                              color: colorTokens.fg.muted,
                            }}
                          >
                            {m.condition1}
                          </Box>
                        )}
                      </Box>
                      {m.lines[0].content !== title && (
                        <Box
                          sx={{
                            mt: 0.25,
                            fontSize: scaledFontSize('label'),
                            color: colorTokens.fg.muted,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {m.lines[0].content}
                        </Box>
                      )}
                    </Box>
                    {/* 区分を持たないマスタなので、押した行の直下で挿入先を選ばせる
                        (= ダイアログを重ねない)。 */}
                    {isOpen && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          flexWrap: 'wrap',
                          px: 1,
                          pb: 0.75,
                        }}
                      >
                        {SOAP_TYPE_ORDER.map((type) => (
                          <Box
                            key={type}
                            component="button"
                            type="button"
                            disabled={isInserting}
                            aria-label={`${type} に挿入`}
                            onClick={() => void insertText(m, type)}
                            sx={sectionButtonSx}
                          >
                            {type}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )
              })}
          {/* 空表示は括弧付きの最小文字列に固定する (段落文にしない). */}
          {(tab === 'sets' ? visibleSets : visibleTexts).length === 0 && (
            <Box
              sx={{
                px: 1,
                py: 2,
                fontSize: scaledFontSize('label'),
                color: colorTokens.fg.muted,
              }}
            >
              (該当なし)
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
